import React, { useEffect, useRef, useState } from 'react';
import 'react-gallery-carousel/dist/index.css';
import PostFeedView from '../../components/PostView/PostFeedView';
import FeedLoader from './FeedLoader';
import { openNotification, Loader } from 'tt-ui-lib/core';
import { useSelectedContext } from 'context/contextSelector';
import { getPostsList } from '../../api/posts/endpoints/posts';
import { allowedReactions } from '../../constants/addPost';
import Cookies from 'js-cookie';
import styles from './PostsList.module.scss';

export const PostsList = ({ pageType, isFetchPosts, setIsFetchPosts }) => {
  const { user } = useSelectedContext((context) => context.user);
  const [posts, setPosts] = useState([]);
  const [postsPage, setPostsPage] = useState(1);
  const [postsCount, setPostsCount] = useState(0);
  const [postsTotal, setPostsTotal] = useState(0);
  const [postsNewLoading, setPostsNewLoading] = useState(false);
  const [selectedPage, setSelectedPage] = useState(+sessionStorage.getItem('SelectedPage'));
  const [postId, setPostId] = useState(sessionStorage.getItem('SelectedPostId'));
  const [scrollingLoading, setScrollingLoading] = useState(false);
  const postRefs = useRef({});

  useEffect(() => {
    setTimeout(() => {
      sessionStorage.removeItem('SelectedPage');
    }, 1000);
  }, []);

  useEffect(() => {
    if (selectedPage && user.id) {
      setScrollingLoading(true);
    }
    const fetchPostsToSelectedPage = async () => {
      if (selectedPage > 1) {
        setPostsNewLoading(true);
        try {
          const requests = [];
          for (let page = 1; page <= selectedPage; page++) {
            requests.push(getPostsList(user.id, page));
          }

          const responses = await Promise.all(requests);

          const allPosts = responses
            .filter((response) => response?.status === 200 && response?.data?.data)
            .flatMap((response) =>
              response.data.data.map((item) => ({
                ...item,
                current_page: response.data.current_page,
              }))
            );

          setPosts(allPosts || []);
          setPostsTotal(responses[0]?.data?.total || 0);
        } catch (error) {
          openNotification({
            message: 'Error while fetching feed. Try again later.',
            type: 'error',
          });
        } finally {
          setPostsNewLoading(false);
        }
      }
    };
    if (selectedPage && postId) {
      fetchPostsToSelectedPage();
    } else {
      setScrollingLoading(false);
      setSelectedPage(1);
    }
  }, [selectedPage, user]);

  const getPostsFromApi = (page = 1, onFinally = null) => {
    getPostsList(user.id, page)
      .then((response) => {
        if (response?.status === 200 && response?.data?.data) {
          const updatedData = response.data.data.map((item) => ({
            ...item,
            current_page: response.data.current_page,
          }));
          if (pageType === 'group_blog') {
            setPosts(response?.data.slice(0, 2));
          } else if (page === 1) {
            setPosts(updatedData || []);
          } else {
            setPosts((prevPosts) => [...prevPosts, ...updatedData]);
          }

          setPostsTotal(response.data.total);
        } else {
          openNotification({
            message: 'Error while fetching feed. Try again later.',
            type: 'error',
          });
        }
      })
      .catch(() => {
        openNotification({
          message: 'Error while fetching feed. Try again later.',
          type: 'error',
        });
      })
      .finally(() => {
        if (onFinally) {
          onFinally();
        }
      });
  };

  const handleScroll = () => {
    const container = document.querySelector('#dashboardContent');

    if (
      window.innerHeight + container.scrollTop + 1 >= container.scrollHeight &&
      !postsNewLoading
    ) {
      setPostsPage((prev) => prev + 1);
    }
  };

  useEffect(() => {
    const container = document.querySelector('#dashboardContent');
    container?.addEventListener('scroll', handleScroll);
    return () => {
      container?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (posts) {
      setPostsCount(posts.length);
    }
  }, [posts]);

  useEffect(() => {
    if (
      isFetchPosts &&
      (!Cookies.get('access_token') || user.id) &&
      (!selectedPage || selectedPage === 1)
    ) {
      getPostsFromApi(1);
      setIsFetchPosts(false);
    }
  }, [isFetchPosts, user]);

  useEffect(() => {
    if (postsPage !== 1 && postsCount < postsTotal && postsPage !== selectedPage) {
      setPostsNewLoading(true);
      getPostsFromApi(postsPage, () => setPostsNewLoading(false));
    }
  }, [postsPage]);

  useEffect(() => {
    if (postId && posts.length > 0) {
      const timeout = setTimeout(() => {
        const targetPost = postRefs.current[postId];
        if (targetPost) {
          targetPost.scrollIntoView({ block: 'center' });
          setPostId(0);
          setPostsPage(selectedPage);
          sessionStorage.removeItem('SelectedPage');
          sessionStorage.removeItem('SelectedPostId');
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  setScrollingLoading(false);
                  observer.disconnect();
                }
              });
            },
            { threshold: 0.5 }
          );

          observer.observe(targetPost);
        } else {
          setScrollingLoading(false);
        }
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [postId, posts]);

  const onDeletePost = () => {
    setIsFetchPosts(true);
  };

  return posts?.length > 0 ? (
    <div className={styles.feed}>
      {posts.map(
        (element, index) =>
          // console.log(index, element);
          element.user && (
            <div
              key={`${index + 1}`}
              ref={(el) => {
                if (el) {
                  postRefs.current[element.id] = el;
                }
              }}
            >
              <PostFeedView
                postInstance={element}
                postBody={element.body}
                user={user}
                allowedReactions={allowedReactions}
                onDeletePost={onDeletePost}
              />
            </div>
          )
      )}
      <div className={styles.scrollLoading}>
        {scrollingLoading && <FeedLoader skeletonsCount={6} />}
      </div>
      <div className={styles.feedPartLoader}>{postsNewLoading && <Loader />}</div>
    </div>
  ) : (
    <FeedLoader />
  );
};
